<template>
  <div class="p-1">
    <div v-if="comments" class="mb-1">
      <div v-if="comments.length === 0" class="text-center">
        <p>{{ $t("grid.slots.info.no_comments_yet") }}</p>
      </div>
      <div v-else>
        <div v-for="(comment, index) in comments" :key="comment.id" class="comment-item">
          <p class="comment-text lh-base" v-html="comment.comment" />
          <div class="d-flex justify-content-between align-items-center mt-2">
            <p class="fst-italic small mb-0">
              <span class="fw-medium">{{ commentAuthor(comment) }}</span>
              -
              <span>{{ parseDate(comment.created_at) }}</span>
            </p>
            <a
              v-if="comment.file"
              :href="comment.file"
              target="_blank"
              class="comment-attachment"
              v-b-tooltip.hover
              :title="$t('actions.download_file')"
            >
              <feather-icon icon="PaperclipIcon" size="18" />
            </a>
          </div>
          <hr v-if="index < comments.length - 1" class="my-3" />
        </div>
      </div>
    </div>

    <div v-else class="text-center py-3">
      <b-spinner label="Cargando..." variant="primary" />
    </div>

    <div v-if="$can('create', 'Comment')">
      <b-form-group
        :label="`${$t('actions.add')} ${$tc('alarm.comment', 1)}`"
        label-for="comment-textarea"
        class="mt-1 text-capitalize"
      >
        <b-form-textarea
          id="comment-textarea"
          v-model="commentInput"
          :placeholder="$t('grid.slots.info.write_comment_here')"
          rows="3"
        />
      </b-form-group>

      <div class="dropzone-wrapper">
        <vue-dropzone
          :id="`dp_${idAlarm}`"
          ref="dropzone"
          :options="dropzoneOptions"
          :include-styling="false"
          :use-custom-slot="true"
          @vdropzone-success="addFile"
          @vdropzone-removed-file="removeFile"
          @vdropzone-max-files-exceeded="maxFilesExceeded"
        >
          <div class="dropzone-custom-content-custom">
            <feather-icon icon="UploadCloudIcon" size="30" class="text-primary" />
            <p class="dropzone-custom-title mt-1 mb-0 text-primary">
              Arrastra y suelta o haz click para añadir un archivo
            </p>
          </div>
        </vue-dropzone>
      </div>

      <b-button
        variant="primary"
        class="mt-1 w-100 fw-semibold"
        type="button"
        @click="addComment()"
        :disabled="!commentInput.trim()"
      >
        <feather-icon icon="SendIcon" size="16" class="mr-1" />
        <span class="text-capitalize">{{ $t("actions.send") }}</span>
      </b-button>
    </div>
  </div>
</template>

<script>
import { BSpinner, BFormGroup, BFormTextarea, BButton, VBTooltip } from "bootstrap-vue";
import store from "@/store";
import { ref, nextTick } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import dateParser from "@/libs/date-parser";
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import { useUtils as useI18nUtils } from "@core/libs/i18n";

export default {
  props: {
    idAlarm: Number,
  },
  components: {
    BSpinner,
    BFormGroup,
    BFormTextarea,
    BButton,
    vueDropzone: vue2Dropzone,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  setup(props) {
    const comments = ref(null);
    const commentInput = ref("");
    const toast = useToast();
    const files = ref(null);
    const dropzone = ref();

    const { t } = useI18nUtils();

    const loadData = () => {
      store
        .dispatch("comments/getAll", { alarm: props.idAlarm })
        .then((response) => {
          const list = Array.isArray(response?.data) ? response.data : [];
          comments.value = list.sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          );
          if (dropzone.value) {
            dropzone.value.removeAllFiles();
          }
        })
        .catch((error) => {
          comments.value = [];
          toast({
            component: ToastificationContent,
            props: {
              title: error?.response?.data?.message || t("errors.generic"),
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const addComment = () => {
      if (!commentInput.value.trim()) return;

      store
        .dispatch("comments/add", {
          id_alarm: props.idAlarm,
          comment: commentInput.value,
        })
        .then((response) => {
          // Si hay archivos en la cola, los procesamos
          if (dropzone.value.getQueuedFiles().length > 0) {
            dropzone.value.setOption(
              "url",
              `${window.__env.VUE_APP_BASE_URL}/v1/comment/file/${response.data.id}`
            );
            dropzone.value.processQueue();
          }

          toast({
            component: ToastificationContent,
            props: {
              title: response.message,
              icon: "CheckIcon",
              variant: "success",
            },
          });
          commentInput.value = "";
          // Recargar comentarios después de un breve retraso para permitir la subida del archivo
          setTimeout(loadData, 1500);
        })
        .catch((error) => {
          toast({
            component: ToastificationContent,
            props: {
              title: error?.response?.data?.message || t("errors.generic"),
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    loadData();

    const parseDate = (date) => {
      return dateParser.parseDate(date);
    };

    const commentAuthor = (comment) => {
      const user = comment?.user;
      return user?.name || user?.email || user?.user || "—";
    };

    const template = () => {
      return `<div class="dz-preview dz-file-preview">
              <div class="dz-image"><div data-dz-thumbnail-bg></div></div>
              <div class="dz-details">
                <div class="dz-filename"><span data-dz-name></span></div>
                <div class="dz-size"><span data-dz-size></span></div>
              </div>
              <div class="dz-progress"><span class="dz-upload" data-dz-uploadprogress></span></div>
              <div class="dz-error-message"><span data-dz-errormessage></span></div>
              <a href="#" class="dz-remove" data-dz-remove>
                <i class="feather icon-x-circle"></i>
              </a>
            </div>`;
    };

    const headers = () => {
      const token = localStorage.getItem("accessToken");
      return {
        Authorization: `Bearer ${token}`,
      };
    };

    const addFile = () => {};
    const removeFile = () => {};

    const maxFilesExceeded = (file) => {
      dropzone.value.removeFile(file);
    };

    return {
      comments,
      addComment,
      commentInput,
      parseDate,
      commentAuthor,
      dropzoneOptions: {
        url: `${window.__env.VUE_APP_BASE_URL}/v1/comment/file/`,
        headers: headers(),
        maxFiles: 1,
        paramName: "file",
        addRemoveLinks: true,
        dictRemoveFile: t("actions.delete"),
        dictMaxFilesExceeded: t("messages.error.max_files_exceeded"),
        previewTemplate: template(),
        autoProcessQueue: false,
      },
      addFile,
      removeFile,
      dropzone,
      maxFilesExceeded,
    };
  },
};
</script>

<style lang="scss" scoped>
// Estilo para los comentarios individuales
.comment-item {
  .comment-text {
    color: #343a40;
    white-space: pre-wrap; // Mantiene los saltos de línea del texto
    word-break: break-word;
  }
  .comment-attachment {
    color: var(--primary);
    &:hover {
      opacity: 0.8;
    }
  }
}

// Estilo para el contenedor de Dropzone
.dropzone-wrapper {
  border: 2px dashed var(--primary); // Borde punteado azul primario
  border-radius: 0.75rem;
  margin-top: 1rem;
  background-color: #f8f9fa; // Fondo sutil
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #e9ecef;
  }
}

// Estilos para el componente vue-dropzone
.dropzone-custom-content-custom {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  cursor: pointer;

  .dropzone-custom-title {
    font-weight: 500;
  }
}

// Estilos para el tema oscuro
.dark-layout {
  .comment-item .comment-text {
    color: #d0d2d6;
  }
  .dropzone-wrapper {
    background-color: #283046;
    border-color: #007bff;
    &:hover {
      background-color: #343d55;
    }
  }
}

// Anulación de estilos de vue2-dropzone para que se vea limpio
::v-deep .dz-preview {
  margin: 10px !important;
  .dz-remove {
    font-size: 1.1rem !important;
    color: #d9534f !important;
    &:hover {
      color: #c9302c !important;
    }
  }
}
</style>
